/**
 * Pill navigation for event editor sections.
 * Horizontal scroll, current section highlight, fill indicator per section.
 */

import { useRef, useEffect, type ReactNode } from "react";
import { IconCheck } from "../../../../components/Icons.tsx";
import { haptic } from "../../../../lib/telegram.ts";

export interface SectionNavItem {
  id: string;
  label: string;
  filled: boolean;
  icon?: ReactNode;
}

export interface EventEditorNavProps {
  sections: SectionNavItem[];
  activeSectionId: string | null;
  onSectionClick: (id: string) => void;
  className?: string;
}

export function EventEditorNav({
  sections,
  activeSectionId,
  onSectionClick,
  className = "",
}: EventEditorNavProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const container = scrollRef.current;
    const activeEl = activeRef.current;
    if (!container || !activeEl || !activeSectionId) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = activeEl.getBoundingClientRect();
    const scrollLeft = container.scrollLeft + (elRect.left - containerRect.left) - containerRect.width / 2 + elRect.width / 2;
    container.scrollTo({ left: Math.max(0, scrollLeft), behavior: "smooth" });
  }, [activeSectionId]);

  return (
    <div
      ref={scrollRef}
      className={`flex gap-2 overflow-x-auto overflow-y-hidden pb-1 -mx-1 scrollbar-none snap-x snap-mandatory ${className}`}
      style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
    >
      {sections.map((section) => {
        const isActive = section.id === activeSectionId;
        return (
          <button
            key={section.id}
            ref={isActive ? activeRef : undefined}
            type="button"
            onClick={() => {
              haptic("light");
              onSectionClick(section.id);
              const el = document.getElementById(section.id);
              el?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-[13px] font-medium whitespace-nowrap snap-start transition-colors ${
              isActive
                ? "bg-[var(--color-accent)] text-white"
                : "bg-slate-100 text-slate-700 active:bg-slate-200"
            }`}
          >
            {section.filled && (
              <span className={isActive ? "text-white" : "text-emerald-500"}>
                <IconCheck className="w-3.5 h-3.5" />
              </span>
            )}
            {section.label}
          </button>
        );
      })}
    </div>
  );
}
